import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { money, langName } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { CountryField } from "@/components/CountryField";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Check, Lock, MapPin, Tent, UserRound } from "lucide-react";
import { useLang } from "@/i18n";
import { PRINT_LANGS } from "@/lib/printI18n";
import PitchPicker from "@/components/PitchPicker";
import EmailField from "@/components/EmailField";
import DocumentsField from "@/components/DocumentsField";
import OccupantFields, { occupantMissing } from "@/components/reservation/OccupantFields";

const NONE = "__none__";
export const BOOKER_FIELDS = ["first_name", "last_name", "email", "birth_date", "street", "house_number", "postal_code", "city", "country", "passport_number", "passport_country", "passport_place", "card_number", "card_valid_until"];
export const REQUIRED_DEFINITIVE = BOOKER_FIELDS.filter((k) => k !== "card_number" && k !== "card_valid_until");
export const STAY_ITEMS = ["car", "caravan", "mobilhome", "trailer", "tent", "moto", "boat", "mobilhome9", "bungalow", "cabin"];
export const PLATE_ITEMS = ["car", "caravan", "mobilhome", "trailer", "moto", "mobilhome9"];

export const blankCompany = () => ({ on_account: false, name: "", street: "", house_number: "", postal_code: "", city: "", country: "", phone: "", vat_number: "" });

export const blankOccupant = (kind, booker = {}, arrival = "", departure = "") => ({
  kind,
  first_name: "", last_name: "", birth_date: "", passport_number: "", passport_country: "", passport_place: "",
  street: booker.street || "", house_number: booker.house_number || "", postal_code: booker.postal_code || "",
  city: booker.city || "", country: booker.country || "", arrival, departure,
});

export const emptyReservation = (arrival = "") => ({
  guest_id: "", pitch_label: "", zone: "", arrival, departure: "", notes: "", package_id: "",
  booker: { ...Object.fromEntries(BOOKER_FIELDS.map((k) => [k, ""])), language: "nl", phone: "", rood_lintje: false, company: blankCompany() },
  stay: { adults: 1, children: 0, dogs: 0, items: [], counts: {}, power: null, plates: {} },
  occupants: [],
  documents: [], document_ids: [], meter_start: "",
});

export const reservationFromRow = (r, guests = []) => {
  const g = guests.find((x) => x.id === r.guest_id) || {};
  const fallback = { ...Object.fromEntries(Object.entries(g).filter(([k]) => k in emptyReservation().booker)) };
  if (!fallback.first_name && !fallback.last_name && r.guest_name) {
    const [first, ...rest] = r.guest_name.split(" ");
    fallback.first_name = first; fallback.last_name = rest.join(" ");
  }
  return {
    guest_id: r.guest_id || "", pitch_label: r.pitch_label, zone: r.zone, arrival: r.arrival, departure: r.departure, notes: r.notes || "", package_id: r.package_id || "",
    booker: { ...emptyReservation().booker, ...fallback, ...(r.booker || {}) },
    stay: { ...emptyReservation().stay, ...(r.stay || {}) },
    occupants: r.occupants || [],
    documents: r.documents || [], document_ids: r.document_ids || [],
    meter_start: r.meter_start ?? "",
  };
};

export const stayConflict = (items) => {
  const s = new Set(items);
  if (s.has("bungalow") && s.has("cabin")) return "conflict_bungalow_cabin";
  if (["caravan", "mobilhome", "tent"].some((k) => s.has(k)) && (s.has("bungalow") || s.has("cabin"))) return "conflict_unit_accommodation";
  return null;
};

export const missingDefinitive = (form) => REQUIRED_DEFINITIVE.filter((k) => !form.booker[k]);

// Medereizigers die nog niet volledig zijn, als leesbare lijst
export const occupantsIncomplete = (form, t) => {
  const adults = (form.occupants || []).filter((o) => o.kind === "adult");
  const children = (form.occupants || []).filter((o) => o.kind === "child");
  const out = [];
  adults.forEach((o, i) => { if (occupantMissing(o, form.arrival).length) out.push(`${t("tab_adult")} ${i + 2}`); });
  children.forEach((o, i) => { if (occupantMissing(o, form.arrival).length) out.push(`${t("tab_child")} ${i + 1}`); });
  return out;
};

export const MAX_STAY_DAYS = 1500;
export const DATE_MAX = new Date(Date.now() + MAX_STAY_DAYS * 86400000).toISOString().slice(0, 10);
const stayTooLong = (form) => {
  if (!form.arrival || !form.departure) return false;
  return (new Date(form.departure) - new Date(form.arrival)) / 86400000 > MAX_STAY_DAYS;
};

export const blockingReasons = (form, t) => {
  const r = [];
  if (!form.arrival) r.push(t("arrival"));
  if (!form.departure) r.push(t("departure"));
  if (form.arrival && form.departure && form.departure < form.arrival) r.push(t("departure_before_arrival"));
  if (stayTooLong(form)) r.push(t("quote_range_warn"));
  if (!form.pitch_label) r.push(t("chosen_pitch"));
  if (!form.booker.first_name) r.push(t("first_name"));
  if (!form.booker.last_name) r.push(t("last_name"));
  const c = stayConflict(form.stay.items);
  if (c) r.push(t(c));
  return r;
};
export const provisionalOk = (form) => !!(form.pitch_label && form.arrival && form.departure && form.departure >= form.arrival && form.booker.first_name && form.booker.last_name);
export const stayComplete = (form) => !!(form.pitch_label && form.arrival && form.departure && form.departure >= form.arrival && !stayConflict(form.stay.items) && form.stay.adults >= 1);

function F({ label, children, className = "" }) {
  return <div className={`space-y-1 ${className}`}><Label className="text-[11px] text-muted-foreground">{label}</Label>{children}</div>;
}

function Section({ icon: Icon, step, title, children, right, tone = "" }) {
  return (
    <div className={`rounded-xl border border-border bg-card shadow-sm p-4 space-y-3 ${tone}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="h-7 w-7 rounded-full bg-primary text-primary-foreground grid place-items-center text-xs font-bold">{step}</span>
          <Icon className="h-4 w-4 text-primary" />
          <p className="font-display font-bold text-sm text-foreground">{title}</p>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

export default function ReservationForm({ form, setForm, guests, zones, pitches = [], highlightMissing = false, withDocuments = false, stayApproved = false, withOccupants = false }) {
  const { t, lang } = useLang();
  const b = form.booker;
  const st = form.stay;
  const [approved, setApproved] = useState(stayApproved);
  const [tab, setTab] = useState("booker");
  const setB = (k) => (e) => setForm({ ...form, booker: { ...b, [k]: e.target.value } });
  const setS = (k, v) => setForm({ ...form, stay: { ...st, [k]: v } });
  const toggle = (k) => setS("items", st.items.includes(k) ? st.items.filter((x) => x !== k) : [...st.items, k]);
  const conflict = stayConflict(st.items);
  const miss = highlightMissing ? new Set(missingDefinitive(form)) : new Set();
  const inv = (k) => (miss.has(k) ? true : undefined);

  // Tarieven: eigen items, pakketten en een live prijsindicatie
  const [tariffItems, setTariffItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [quote, setQuote] = useState(null);
  const [quoteWarn, setQuoteWarn] = useState("");
  useEffect(() => {
    api.get("/tariff-items").then((r) => setTariffItems(r.data)).catch(() => {});
    api.get("/tariff-packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);
  const customItems = useMemo(() => tariffItems.filter((i) => !i.builtin && i.active !== false), [tariffItems]);
  const quoteKey = JSON.stringify([form.zone, form.pitch_label, form.arrival, form.departure, st, form.package_id]);
  useEffect(() => {
    if (!form.arrival || !form.departure || form.departure < form.arrival) { setQuote(null); setQuoteWarn(""); return; }
    if (stayTooLong(form)) { setQuote(null); setQuoteWarn(t("quote_range_warn")); return; }
    setQuoteWarn("");
    const id = setTimeout(() => {
      api.post("/tariffs/quote-preview", {
        zone: form.zone || "", pitch_label: form.pitch_label || "", arrival: form.arrival, departure: form.departure,
        stay: { adults: st.adults, children: st.children, dogs: st.dogs, items: st.items, counts: st.counts || {}, power: st.power },
        package_id: form.package_id || null, lang: b.language || "nl",
      }).then((r) => { setQuote(r.data); setQuoteWarn(""); })
        .catch((e) => { setQuote(null); setQuoteWarn(e.response?.data?.detail || ""); });
    }, 400);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteKey]);
  const setCount = (k, v) => setS("counts", { ...(st.counts || {}), [k]: v });
  const sortedGuests = useMemo(
    () => [...guests].sort((a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`)),
    [guests]
  );

  const extraAdults = withOccupants ? Math.max(0, (st.adults || 1) - 1) : 0;
  const children = withOccupants ? Math.max(0, st.children || 0) : 0;
  const occAdults = (form.occupants || []).filter((o) => o.kind === "adult");
  const occChildren = (form.occupants || []).filter((o) => o.kind === "child");

  // Aantal tabbladen volgt de aantallen uit het verblijf
  useEffect(() => {
    if (occAdults.length === extraAdults && occChildren.length === children) return;
    const next = [
      ...Array.from({ length: extraAdults }, (_, i) => occAdults[i] || blankOccupant("adult", b, form.arrival, form.departure)),
      ...Array.from({ length: children }, (_, i) => occChildren[i] || blankOccupant("child", b, form.arrival, form.departure)),
    ];
    setForm({ ...form, occupants: next });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraAdults, children]);

  // Adres en periode worden pas bij het openen samengevoegd, zodat later ingevulde boekergegevens ook overgenomen worden
  const withDefaults = (o) => ({
    ...o,
    street: o.street || b.street || "",
    house_number: o.house_number || b.house_number || "",
    postal_code: o.postal_code || b.postal_code || "",
    city: o.city || b.city || "",
    country: o.country || b.country || "",
    arrival: o.arrival || form.arrival || "",
    departure: o.departure || form.departure || "",
  });

  const setOccupant = (kind, idx) => (value) => {
    let a = 0;
    let c = 0;
    const next = (form.occupants || []).map((o) => {
      if (o.kind === "adult") { const hit = kind === "adult" && a === idx; a += 1; return hit ? value : o; }
      const hit = kind === "child" && c === idx; c += 1; return hit ? value : o;
    });
    setForm({ ...form, occupants: next });
  };

  const pickGuest = (id) => {
    if (id === NONE) return setForm({ ...form, guest_id: "" });
    const g = guests.find((x) => x.id === id);
    setForm({ ...form, guest_id: id, booker: { ...b, ...Object.fromEntries(Object.entries(g || {}).filter(([k]) => k in b)) } });
  };

  const comp = b.company || blankCompany();
  const setC = (k) => (e) => setForm({ ...form, booker: { ...b, company: { ...comp, [k]: e.target.value } } });
  const tabs = [
    { key: "booker", label: t("booker") },
    ...(comp.on_account ? [{ key: "company", label: t("company") }] : []),
    ...occAdults.map((_, i) => ({ key: `adult-${i}`, label: `${t("tab_adult")} ${i + 2}`, kind: "adult", idx: i })),
    ...occChildren.map((_, i) => ({ key: `child-${i}`, label: `${t("tab_child")} ${i + 1}`, kind: "child", idx: i })),
  ];
  const active = tabs.find((x) => x.key === tab) || tabs[0];
  const stayOk = stayComplete(form);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-4 items-start">
      {/* Links: verblijf */}
      <Section step="1" icon={Tent} title={t("your_stay")} tone="border-primary/50 bg-secondary/30">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <F label={t("arrival")}><Input required type="date" autoFocus max={DATE_MAX} value={form.arrival} onChange={(e) => setForm({ ...form, arrival: e.target.value })} data-testid="reservation-arrival" /></F>
          <F label={t("departure")}><Input required type="date" min={form.arrival} max={DATE_MAX} value={form.departure} onChange={(e) => setForm({ ...form, departure: e.target.value })} data-testid="reservation-departure" /></F>
          <F label={t("chosen_pitch")}>
            <PitchPicker value={form.pitch_label} pitches={pitches} zones={zones} arrival={form.arrival} departure={form.departure} currentLabel={form.pitch_label}
              onPick={(label, zone) => setForm({ ...form, pitch_label: label, zone })} />
          </F>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {["adults", "children", "dogs"].map((k) => (
            <F key={k} label={t(`stay_${k}`)}><Input type="number" min={k === "adults" ? "1" : "0"} value={st[k]} onChange={(e) => setS(k, Number(e.target.value))} data-testid={`stay-${k}`} /></F>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {STAY_ITEMS.map((k) => (
            <div key={k} className={`flex items-center gap-2 rounded-md border px-2 py-1 text-sm transition-colors ${st.items.includes(k) ? "border-primary bg-secondary" : "border-border hover:bg-muted"}`} data-testid={`stay-item-${k}`}>
              <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                <input type="checkbox" className="accent-primary" checked={st.items.includes(k)} onChange={() => toggle(k)} />
                <img src={`${process.env.PUBLIC_URL || ""}/print/${k}.png`} alt="" className="h-5 w-8 object-contain opacity-80" />
                <span className="truncate">{t(`stay_${k}`)}</span>
              </label>
              {PLATE_ITEMS.includes(k) && (
                <Input value={st.plates?.[k] || ""} placeholder={t("plate")} disabled={!st.items.includes(k)} className="h-7 w-28 text-xs uppercase shrink-0 disabled:opacity-40" data-testid={`stay-plate-${k}`}
                  onChange={(e) => setS("plates", { ...(st.plates || {}), [k]: e.target.value.toUpperCase() })} />
              )}
            </div>
          ))}
        </div>
        {customItems.length > 0 && (
          <div className="grid grid-cols-2 gap-1.5" data-testid="stay-custom-items">
            {customItems.map((i) => (
              i.countable ? (
                <F key={i.key} label={i.label}>
                  <Input type="number" min="0" value={(st.counts || {})[i.key] || 0} onChange={(e) => setCount(i.key, Number(e.target.value))} data-testid={`stay-count-${i.key}`} />
                </F>
              ) : (
                <div key={i.key} className={`flex items-center gap-2 rounded-md border px-2 py-1 text-sm ${st.items.includes(i.key) ? "border-primary bg-secondary" : "border-border hover:bg-muted"}`} data-testid={`stay-item-${i.key}`}>
                  <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                    <input type="checkbox" className="accent-primary" checked={st.items.includes(i.key)} onChange={() => toggle(i.key)} />
                    <span className="truncate">{i.label}</span>
                  </label>
                </div>
              )
            ))}
          </div>
        )}
        {conflict && <p className="text-xs text-rose-600 font-medium" data-testid="stay-conflict">{t(conflict)}</p>}
        <div>
          <Label className="text-xs">{t("power")}</Label>
          <div className="flex gap-2 mt-1">
            {[null, "6", "10"].map((p) => (
              <button type="button" key={String(p)} onClick={() => setS("power", p)} data-testid={`stay-power-${p || "none"}`}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${st.power === p ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
                {p ? `${p} Amp` : t("none_selected")}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 items-end">
          <F label={t("meter_arrival")}>
            <Input type="number" min="0" step="0.1" value={form.meter_start ?? ""}
              onChange={(e) => setForm({ ...form, meter_start: e.target.value })} data-testid="reservation-meter-start" />
          </F>
          {(() => {
            const pitch = pitches.find((p) => p.label === form.pitch_label);
            if (!pitch || pitch.meter_last == null) return <p className="text-[11px] text-muted-foreground pb-2" data-testid="meter-last-none">{t("meter_last_unknown")}</p>;
            return (
              <button type="button" className="text-[11px] text-primary underline pb-2 text-left" data-testid="meter-use-last"
                onClick={() => setForm({ ...form, meter_start: pitch.meter_last })}>
                {t("meter_last_known")}: {pitch.meter_last} kWh → {t("use")}
              </button>
            );
          })()}
        </div>
        <div className="grid grid-cols-1 gap-2">
          <F label={t("tariff_package")}>
            <Select value={form.package_id || NONE} onValueChange={(v) => setForm({ ...form, package_id: v === NONE ? "" : v })}>
              <SelectTrigger data-testid="stay-package"><SelectValue placeholder={t("tariff_no_package")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>{t("tariff_no_package")}</SelectItem>
                {packages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <div className="rounded-lg border border-border bg-card px-3 py-2" data-testid="stay-quote">
            <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
              <span>{t("tariff_price_estimate")}</span>
              <span className="font-display text-base text-primary tabular-nums" data-testid="stay-quote-total">{money(quote?.subtotal || 0)}</span>
            </div>
            {quoteWarn ? (
              <p className="mt-1 text-[11px] text-rose-500" data-testid="stay-quote-warn">{quoteWarn}</p>
            ) : quote?.lines?.length ? (
              <>
              {quote.per_month ? (
                <div className="mt-1 flex items-center justify-between text-[11px] font-medium text-emerald-600 dark:text-emerald-400" data-testid="stay-quote-permonth">
                  <span>{t("quote_per_month")}</span>
                  <span className="tabular-nums">± {money(quote.per_month)}</span>
                </div>
              ) : null}
              <ul className="mt-1 space-y-0.5 max-h-28 overflow-auto cb-scroll">
                {quote.lines.map((l, i) => (
                  <li key={i} className="flex justify-between gap-2 text-[11px] text-muted-foreground" data-testid={`stay-quote-line-${i}`}>
                    <span className="truncate">{l.description}</span>
                    <span className="tabular-nums shrink-0">{money(l.qty * l.unit_price)}</span>
                  </li>
                ))}
              </ul>
              </>
            ) : <p className="text-[11px] text-muted-foreground mt-1">{t("tariff_no_tariffs")}</p>}
          </div>
        </div>
        <label className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm cursor-pointer hover:bg-muted" data-testid="company-toggle">
          <input type="checkbox" className="accent-primary" checked={!!b.company?.on_account}
            onChange={(e) => setForm({ ...form, booker: { ...b, company: { ...(b.company || blankCompany()), on_account: e.target.checked } } })} />
          <Building2 className="h-4 w-4 text-muted-foreground" />{t("company_on_account")}
        </label>
        <F label={t("note")}><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="reservation-notes" /></F>
        {withDocuments && (
          <DocumentsField docs={form.documents || []} setDocs={(d) => setForm({ ...form, documents: d, document_ids: d.map((x) => x.id) })} guestId={form.guest_id} />
        )}
        {withOccupants && (
        <div className="flex items-center gap-3 pt-1">
          <Button type="button" onClick={() => setApproved(true)} disabled={!stayOk || approved} data-testid="stay-complete-button">
            <Check className="h-4 w-4 mr-1" />{approved ? t("stay_approved") : t("complete")}
          </Button>
          <p className="text-[11px] text-muted-foreground">{approved ? t("stay_approved_hint") : t("complete_hint")}</p>
        </div>
        )}
      </Section>

      {/* Rechts: boeker + medereizigers in tabbladen */}
      <Section step="2" icon={UserRound} title={t("main_booker")} right={
        tab === "booker" ? (
          <Select value={form.guest_id || NONE} onValueChange={pickGuest}>
            <SelectTrigger className="h-7 w-44 text-xs" data-testid="reservation-guest" tabIndex={-1}><SelectValue placeholder={t("pick_existing_guest")} /></SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value={NONE}>{t("new_guest")}</SelectItem>
              {sortedGuests.map((g) => <SelectItem key={g.id} value={g.id}>{g.last_name} {g.first_name}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : null
      }>
        {withOccupants && (
        <div className="flex flex-wrap gap-1.5 border-b border-border pb-2">
          {tabs.map((x) => {
            const locked = x.key !== "booker" && !approved;
            const occ = x.kind === "adult" ? occAdults[x.idx] : x.kind === "child" ? occChildren[x.idx] : null;
            const incomplete = occ && occupantMissing(occ, form.arrival).length > 0;
            return (
              <button type="button" key={x.key} onClick={() => !locked && setTab(x.key)} disabled={locked} data-testid={`occupant-tab-${x.key}`}
                className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                  tab === x.key ? "bg-primary text-primary-foreground border-primary" : locked ? "border-border text-slate-300 cursor-not-allowed" : "border-border hover:bg-muted"}`}>
                {locked && <Lock className="h-3 w-3" />}
                {x.label}
                {!locked && incomplete && <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />}
                {!locked && occ && !incomplete && <Check className="h-3 w-3" />}
              </button>
            );
          })}
        </div>
        )}

        {active?.key === "company" ? (
          <div className="grid grid-cols-2 gap-2" data-testid="company-fields">
            <F label={t("company_name")} className="col-span-2"><Input value={comp.name} onChange={setC("name")} data-testid="company-name" /></F>
            <F label={t("u_street")} className="col-span-2"><Input value={comp.street} onChange={setC("street")} data-testid="company-street" /></F>
            <F label={t("u_house")}><Input value={comp.house_number} onChange={setC("house_number")} data-testid="company-house" /></F>
            <F label={t("u_postal")}><Input value={comp.postal_code} onChange={setC("postal_code")} data-testid="company-postal" /></F>
            <F label={t("u_city")}><Input value={comp.city} onChange={setC("city")} data-testid="company-city" /></F>
            <F label={t("country")}><CountryField value={comp.country} onChange={setC("country")} testid="company-country" /></F>
            <F label={t("phone")}><Input value={comp.phone} onChange={setC("phone")} data-testid="company-phone" /></F>
            <F label={t("setup_vat_number")}><Input value={comp.vat_number} onChange={setC("vat_number")} data-testid="company-vat" /></F>
            <p className="col-span-2 text-[11px] text-muted-foreground">{t("company_hint")}</p>
          </div>
        ) : active?.key === "booker" ? (
          <div className="grid grid-cols-2 gap-2">
            <F label={t("first_name")}><Input required value={b.first_name} onChange={setB("first_name")} data-invalid={inv("first_name")} data-testid="booker-first-name" /></F>
            <F label={t("last_name")}><Input required value={b.last_name} onChange={setB("last_name")} data-invalid={inv("last_name")} data-testid="booker-last-name" /></F>
            <F label={t("email")} className="col-span-2"><EmailField required invalid={inv("email")} value={b.email} onChange={setB("email")} testid="booker-email" /></F>
            <F label={t("u_birth")}><Input required type="date" max={new Date().toISOString().slice(0, 10)} value={b.birth_date} onChange={setB("birth_date")} data-invalid={inv("birth_date")} data-testid="booker-birth" /></F>
            <F label={t("doc_language")}>
              <Select value={b.language || "nl"} onValueChange={(v) => setForm({ ...form, booker: { ...b, language: v } })}>
                <SelectTrigger data-testid="booker-language"><SelectValue /></SelectTrigger>
                <SelectContent>{PRINT_LANGS.map((l) => <SelectItem key={l.code} value={l.code}>{langName(l.code, lang)}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label={t("u_street")} className="col-span-2"><Input required value={b.street} onChange={setB("street")} data-invalid={inv("street")} data-testid="booker-street" /></F>
            <F label={t("u_house")}><Input required value={b.house_number} onChange={setB("house_number")} data-invalid={inv("house_number")} data-testid="booker-house" /></F>
            <F label={t("u_postal")}><Input required value={b.postal_code} onChange={setB("postal_code")} data-invalid={inv("postal_code")} data-testid="booker-postal" /></F>
            <F label={t("u_city")}><Input required value={b.city} onChange={setB("city")} data-invalid={inv("city")} data-testid="booker-city" /></F>
            <F label={t("country")}><CountryField required invalid={inv("country")} value={b.country} onChange={setB("country")} testid="booker-country" /></F>
            <F label={t("passport")} className="col-span-2"><Input required value={b.passport_number} onChange={setB("passport_number")} data-invalid={inv("passport_number")} data-testid="booker-passport" /></F>
            <F label={t("issue_country")}><CountryField required invalid={inv("passport_country")} value={b.passport_country} onChange={setB("passport_country")} testid="booker-passport-country" /></F>
            <F label={t("issue_place")}><Input required value={b.passport_place} onChange={setB("passport_place")} data-invalid={inv("passport_place")} data-testid="booker-passport-place" /></F>
            <F label={<>{t("card_number")} <span className="text-[9px] text-muted-foreground">(ASCI, Key Europe, ADAC)</span></>}><Input value={b.card_number} onChange={setB("card_number")} data-testid="booker-card" /></F>
            <F label={t("valid_until")}><Input type="date" value={b.card_valid_until} onChange={setB("card_valid_until")} data-testid="booker-card-valid" /></F>
            <label className="col-span-2 flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground cursor-pointer hover:bg-muted" data-testid="booker-ribbon">
              <input type="checkbox" className="accent-rose-600" checked={!!b.rood_lintje} onChange={(e) => setForm({ ...form, booker: { ...b, rood_lintje: e.target.checked } })} />
              <span className="h-2.5 w-2.5 rounded-full bg-rose-600" />{t("ribbon_guest")}
            </label>
          </div>
        ) : (
          <OccupantFields
            occupant={withDefaults(active.kind === "adult" ? occAdults[active.idx] : occChildren[active.idx])}
            onChange={setOccupant(active.kind, active.idx)}
            arrival={form.arrival}
            departure={form.departure}
            highlightMissing={highlightMissing}
            testPrefix={`occupant-${active.key}`}
          />
        )}

        {withOccupants && tabs.length === 1 && <p className="text-[11px] text-muted-foreground">{t("no_extra_occupants")}</p>}
      </Section>
    </div>
  );
}
